import * as moment from 'moment'
import { http } from '../http'
import compose from 'recompose/compose'
import branch from 'recompose/branch'
import renderComponent from 'recompose/renderComponent'
import withHandlers from 'recompose/withHandlers'
import withStateHandlers from 'recompose/withStateHandlers'
import lifecycle from 'recompose/lifecycle'
import mapProps from 'recompose/mapProps'
import { Spinner } from '../components/Spinner'
import { CardList, IEnhancedCardListProps, MaybeEnabled } from '../components/CardList'
import { ICard } from '../components/Card'
import { CardListError } from '../components/CardListError'

const PAGE_LIMIT = 50

interface IResponse {
  data: {
    data: ICandidate[]
    meta: IMeta
  }
}

interface IMeta {
  contestEnabled: number | false
  limit: number
  offset: number
  total: number
}

interface ICandidate {
  id: string
  attributes: {
    owner: string
    filename: string
    score: number
  }
}

function getImgSrc({ attributes: { owner, filename } }: ICandidate) {
  return `https://api.cappasity.com/api/files/preview/${owner}/w640-h400-cpad/${filename}.jpeg`
}

function getHref({ attributes: { owner, filename } }: ICandidate) {
  return `https://3d.cappasity.com/u/${owner}/${filename}`
}

function remapResponse(res: IResponse) {
  return res.data.data.map(candidate => ({
    img: getImgSrc(candidate),
    href: getHref(candidate),
    score: candidate.attributes.score,
  }))
}

function getError(error: Error) {
  return error.toString()
}

function formatTill(enabled: number | false) {
  if (!enabled) return false

  const diff = moment.duration(moment(enabled).diff(moment()))
  const days = Math.floor(diff.asDays())
  const hours = diff.hours()
  const minutes = diff.minutes()

  return [days && `${days}d`, hours && `${hours}h`, minutes && `${minutes}m`]
    .filter(Boolean)
    .join(' ')
}

const enhance = compose<IEnhancedCardListProps, {}>(
  withStateHandlers(
    {
      enabled: false,
      candidates: [],
      isFetching: true,
      total: 0,
      offset: 0,
      error: null as string | null,
    },
    {
      setMeta: () => meta => ({
        enabled: meta.contestEnabled,
        offset: meta.offset + meta.limit,
        total: meta.total,
      }),
      startFetching: () => () => ({ isFetching: true }),
      stopFetching: ({ candidates }) => newCandidates => ({
        candidates: candidates.concat(newCandidates),
        error: null,
        isFetching: false,
      }),
      fetchError: () => error => ({ error: getError(error), isFetching: false }),
    }
  ),
  withStateHandlers(
    { till: false as MaybeEnabled },
    {
      setTill: (_, { enabled }: { enabled: number | false }) => () => ({
        till: formatTill(enabled),
      }),
    }
  ),
  withHandlers<
    {
      offset: number
      setMeta(meta: IMeta): void
      startFetching(): void
      stopFetching(): void
      fetchError(): void
    },
    { fetchCandidates(): void }
  >({
    fetchCandidates: ({ setMeta, startFetching, stopFetching, fetchError, offset }) => () => {
      startFetching()
      return http
        .get('https://api.cappasity.com/api/capp/candidates/list', {
          params: {
            offset,
            order: 'asc',
            limit: PAGE_LIMIT,
          },
        })
        .then((res: IResponse) => {
          setMeta(res.data.meta)
          return res
        })
        .then(remapResponse)
        .then(stopFetching)
        .catch(fetchError)
    },
  }),
  lifecycle<{ setTill(): void; fetchCandidates(): Promise<any> }, {}, { timeoutId?: number }>({
    componentDidMount() {
      this.props.fetchCandidates().then(() => {
        this.props.setTill()
        this.timeoutId = window.setInterval(this.props.setTill, 2000)
      })
    },
    componentWillUnmount() {
      if (this.timeoutId) window.clearInterval(this.timeoutId)
    },
  }),
  mapProps<
    IEnhancedCardListProps,
    {
      candidates: ICard[]
      isFetching: boolean
      till: MaybeEnabled
      error: string | null
      total: number
      fetchCandidates(): void
    }
  >(({ candidates, isFetching, till, error, total, fetchCandidates }) => ({
    till,
    error,
    isFetching,
    fetchCandidates,
    showMore: candidates.length < total,
    leaders: candidates.slice(0, 3),
    others: candidates.slice(3),
  })),
  branch<IEnhancedCardListProps>(
    props => props.isFetching && !props.leaders.length,
    renderComponent(Spinner)
  ),
  branch<IEnhancedCardListProps>(props => !!props.error, renderComponent(CardListError))
)

export const CardListContainer = enhance(CardList)



// WEBPACK FOOTER //
// ./src/containers/CardListContainer.ts